import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import { AdminActions } from "src/redux/Admin/actions";
import { AdminSelectors } from "src/redux/Admin/selectors";
import { ConfirmActions } from "src/redux/Confirm/actions";
import Button from "src/components/common/Button";
import CheckBox from "src/components/common/CheckBox";
import TagInput from "./TagInput";
import SecretField from "./SecretField";
import UserReportHistory from "./UserReportHistory";
import useUnsavedChangesGuard from "src/utils/useUnsavedChangesGuard";
import styles from "./styles.module.scss";

// Weekday codes for the user-access-report schedule, in MON..SUN order (matches
// the scheduler's parse_weekday and the Platform-tab command schedules).
const WEEKDAYS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

const SUBSCRIPTION_OPTIONS = ["koscom", "markit"];
const SUBSCRIPTION_LABELS = ["KOSCOM", "MARKIT"];
const FILE_FORMAT_OPTIONS = ["KSD", "KSFC", "KSD_CUSTOM", "KSFC_CUSTOM", "TRU_MSG"];
const MAX_CPTY_CODE_LENGTH = 15;
// This page doubles as the onboarding window when the route is /cpties/new: the
// Code becomes editable and mandatory, and the bottom bar is always present.
const NEW_CPTY_SENTINEL = "new";

// KST = UTC+9. Display KST to operators, store UTC in backend.
const KST_OFFSET = 9;
const utcToKst = (utcHour) => (utcHour + KST_OFFSET) % 24;
const kstToUtc = (kstHour) => (kstHour - KST_OFFSET + 24) % 24;
const kstLabel = (utcHour) => {
    const kst = utcToKst(utcHour);
    return `${String(kst).padStart(2, "0")}:00 KST (${String(utcHour).padStart(2, "0")}:00 UTC)`;
};
// UTC hours sorted by KST order (00:00 KST first)
const HOURS_BY_KST = Array.from({ length: 24 }, (_, kst) => kstToUtc(kst));

// Build a change summary string for the confirm dialog
const buildChangeSummary = (original, form, addedRels, removedRels, addedPartners = [], removedPartners = []) => {
    const lines = [];
    const fields = [
        ["cptyName", "Name"],
        ["mailDomains", "Mail Domains"],
        ["genericEnabled", "Generic API"],
        ["genericApiAliasHost", "Generic API Alias Host"],
        ["genericApiAliasPrefix", "Generic API Alias Path"],
        ["hfsEnabled", "HFS API"],
        ["uiEnabled", "UI API"],
        ["hfsId", "HFS ID"],
        ["fileFormats", "Confirmation Basket Formats"],
        ["refDataSubscription", "Market Data Subscriptions"],
        ["useClientPool", "Use Client Pool"],
        ["useConfirmableInventory", "Use Confirmable Inventory"],
        ["expireInventory", "Expire Inventory"],
        ["usePostTrade", "Post-Trade Processing"],
        ["mfaRequired", "Require MFA"],
        ["eaExpireTodayOrTomorrow", "Holds Expire Same Day"],
        ["autoRollUtcHour", "Settlement Auto-Roll Time"],
        ["autoRollInventoryUTCHour", "Inventory Auto-Roll Time"],
        ["eaExpiryUtcHour", "Hold Expiry Time"],
        ["inventorySftpUploadEmails", "Outbox Msg Emails"],
        ["confirmationSftpHost", "Confirmation SFTP Host"],
        ["confirmationSftpPort", "Confirmation SFTP Port"],
        ["confirmationSftpPath", "Confirmation SFTP Path"],
        ["confirmationSftpUserName", "Confirmation SFTP User"],
        ["confirmationSftpFormats", "Confirmation SFTP Formats"],
        ["sftpRecallsHost", "Recalls SFTP Host"],
        ["sftpRecallsUser", "Recalls SFTP User"],
        ["sftpRecallsSecretsPath", "Recalls SFTP Secrets Path"],
        ["sftpRecallsUploadPath", "Recalls SFTP Send Path"],
        ["userReportEnabled", "User-access Report Enabled"],
        ["userReportCadence", "User-access Report Cadence"],
        ["userReportDaysOfWeek", "User-access Report Days"],
        ["userReportNthWeekday", "User-access Report Occurrence"],
        ["userReportTime", "User-access Report Time"],
        ["userReportTimezone", "User-access Report Timezone"],
        ["userReportToEmails", "User-access Report To"],
        ["userReportCcEmails", "User-access Report Cc"],
        ["userReportBccEmails", "User-access Report Bcc"],
    ];
    for (const [key, label] of fields) {
        const oldVal = String(original[key] ?? "");
        const newVal = String(form[key] ?? "");
        if (oldVal !== newVal) {
            const display = (v) => typeof v === "boolean" ? (v ? "Yes" : "No") : (v || "(empty)");
            lines.push(`${label}: ${display(original[key])} => ${display(form[key])}`);
        }
    }
    if (addedRels.length > 0) lines.push(`Known cpties added: ${addedRels.join(", ")}`);
    if (removedRels.length > 0) lines.push(`Known cpties removed: ${removedRels.join(", ")}`);
    if (addedPartners.length > 0) lines.push(`Partners added: ${addedPartners.map(p => p.code).join(", ")}`);
    if (removedPartners.length > 0) lines.push(`Partners removed: ${removedPartners.join(", ")}`);
    return lines.join("\n");
};

// Blank alias fields mean "no alias": trim, and drop them entirely when empty
// so the backend stores absence rather than an empty string. Mutates in place.
const normalizeAlias = (settings) => {
    const host = (settings.genericApiAliasHost || "").trim();
    const prefix = (settings.genericApiAliasPrefix || "").trim();
    if (host) { settings.genericApiAliasHost = host; } else { delete settings.genericApiAliasHost; }
    if (prefix) { settings.genericApiAliasPrefix = prefix; } else { delete settings.genericApiAliasPrefix; }
};

const CptyDetail = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { code } = useParams();
    const isNew = code === NEW_CPTY_SENTINEL;
    const cpty = useSelector(AdminSelectors.SelectedCpty);
    const cpties = useSelector(AdminSelectors.Cpties);
    const loading = useSelector(AdminSelectors.Loading);
    const error = useSelector(AdminSelectors.Error);

    // The address every mail is sent from. A report recipient may not repeat it:
    // the provider refuses a mail whose sender is also a recipient, so the report
    // would never be delivered. Read once; absent until it arrives, which only
    // means the check waits for it rather than blocking the page.
    const [emailFrom, setEmailFrom] = useState("");

    useEffect(() => {
        dispatch(AdminActions.FetchPlatformSettings())
            .then((res) => setEmailFrom(res?.emailFrom || ""))
            .catch(() => setEmailFrom(""));
    }, [dispatch]);

    // Editable form state
    const [form, setForm] = useState({});
    const [originalForm, setOriginalForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [actionMsg, setActionMsg] = useState(null);
    // Onboarding only: the Code is entered here (editable), not read from the route.
    const [codeInput, setCodeInput] = useState("");

    // Secret field changes (write-only, tracked separately)
    const [secretChanges, setSecretChanges] = useState({});
    const handleSecretChange = (fieldName, value) => {
        setSecretChanges(prev => ({ ...prev, [fieldName]: value }));
        setActionMsg(null);
    };

    // Generic API key entry (write-only). genericKeyInput is the plaintext to
    // store on save; "" means the key is left untouched. A freshly generated
    // or pasted value is shown once, then stored server-side as a one-way HMAC
    // and never retrievable again.
    const [genericKeyInput, setGenericKeyInput] = useState("");
    const [showGenericKey, setShowGenericKey] = useState(false);
    const [editingGenericKey, setEditingGenericKey] = useState(false);

    // Relationship changes (deferred until save)
    const [addedRels, setAddedRels] = useState([]);
    const [removedRels, setRemovedRels] = useState([]);
    const [newRelationship, setNewRelationship] = useState("");

    // Partner changes (deferred until save)
    const [addedPartners, setAddedPartners] = useState([]); // [{code, name}]
    const [removedPartners, setRemovedPartners] = useState([]);
    const [newPartnerCode, setNewPartnerCode] = useState("");
    const [newPartnerName, setNewPartnerName] = useState("");

    // Intermediary accounts (separate from CptyConfig, stored in directory)
    const [accounts, setAccounts] = useState([]);
    const [originalAccounts, setOriginalAccounts] = useState([]);
    const [relationshipError, setRelationshipError] = useState(null);
    const [showDropdown, setShowDropdown] = useState(false);

    useEffect(() => {
        // Always need the full cpty list — for onboarding it drives the code/name
        // clash checks; for edit it feeds the Known Counterparties autocomplete.
        dispatch(AdminActions.FetchCpties());
        if (isNew) {
            // No cpty to load; clear any stale selection so partner/account/etc.
            // sections start empty for the new counterparty.
            dispatch(AdminActions.ClearSelectedCpty());
            return undefined;
        }
        dispatch(AdminActions.FetchCptyDetail(code));
        return () => dispatch(AdminActions.ClearSelectedCpty());
    }, [dispatch, code, isNew]);

    const formFromCpty = useCallback((c) => ({
        cptyName: c.cptyName || "",
        mailDomains: c.mailDomains || "",
        hfsId: c.hfsId || "",
        // API access. These three flags and genericKeyConfigured are stripped
        // out of the CptyAdminConfig settings before saving (see handleSave):
        // the toggles ride at the top level of the setup-own-cpty request and
        // genericKeyConfigured is a read-only indicator.
        genericEnabled: !!c.genericEnabled,
        hfsEnabled: !!c.hfsEnabled,
        uiEnabled: c.uiEnabled !== false,
        genericKeyConfigured: !!c.genericKeyConfigured,
        // Generic API alias: dedicated hostname + path prefix routing to this
        // cpty. Optional; only meaningful when Generic API is enabled.
        genericApiAliasHost: c.genericApiAliasHost || "",
        genericApiAliasPrefix: c.genericApiAliasPrefix || "",
        fileFormats: c.fileFormats || "",
        refDataSubscription: c.refDataSubscription || "",
        useClientPool: !!c.useClientPool,
        useConfirmableInventory: !!c.useConfirmableInventory,
        expireInventory: !!c.expireInventory,
        usePostTrade: !!c.usePostTrade,
        mfaRequired: !!c.mfaRequired,
        eaExpireTodayOrTomorrow: !!c.eaExpireTodayOrTomorrow,
        autoRollUtcHour: c.autoRollUtcHour || 0,
        autoRollInventoryUTCHour: c.autoRollInventoryUTCHour || "",
        eaExpiryUtcHour: c.eaExpiryUtcHour || "",
        // Notification emails (stored as comma-separated in backend, shown as tags)
        inventorySftpUploadEmails: (c.inventorySftpUploadEmails || []).join(","),
        clientPoolUploadEmails: (c.clientPoolUploadEmails || []).join(","),
        // User-access report: per-cpty schedule + recipients
        userReportEnabled: !!c.userReportEnabled,
        userReportCadence: c.userReportCadence || "monthly",
        // Monthly cadence fires on the Nth <weekday> of the month, so a weekday is
        // mandatory. The <select> only defaults MON in its display; seed the stored
        // value too (here and on cadence change) so it is actually saved.
        userReportDaysOfWeek:
            (c.userReportDaysOfWeek || []).join(",")
            || ((c.userReportCadence || "monthly") === "monthly" ? "MON" : ""),
        userReportNthWeekday: c.userReportNthWeekday ? String(c.userReportNthWeekday) : "",
        userReportTime: c.userReportTime || "",
        userReportTimezone: c.userReportTimezone || "Asia/Seoul",
        userReportToEmails: (c.userReportToEmails || []).join(","),
        userReportCcEmails: (c.userReportCcEmails || []).join(","),
        userReportBccEmails: (c.userReportBccEmails || []).join(","),
        // Confirmation SFTP
        confirmationSftpHost: c.confirmationSftpHost || "",
        confirmationSftpPort: c.confirmationSftpPort || "",
        confirmationSftpPath: c.confirmationSftpPath || "",
        confirmationSftpUserName: c.confirmationSftpUserName || "",
        confirmationSftpFormats: c.confirmationSftpFormats || "",
        // Recalls SFTP
        sftpRecallsHost: c.sftpRecallsHost || "",
        sftpRecallsUser: c.sftpRecallsUser || "",
        sftpRecallsSecretsPath: c.sftpRecallsSecretsPath || "",
        sftpRecallsUploadPath: c.sftpRecallsUploadPath || "",
    }), []);

    // Initialize form when cpty loads (edit), or once with empty defaults (onboard).
    useEffect(() => {
        if (isNew) {
            const f = formFromCpty({});
            f.mfaRequired = true; // 2FA defaults on for a newly onboarded cpty
            setForm(f);
            setOriginalForm(f);
            setAccounts([]);
            setOriginalAccounts([]);
            return;
        }
        if (cpty) {
            const f = formFromCpty(cpty);
            setForm(f);
            setOriginalForm(f);
            setAddedRels([]);
            setRemovedRels([]);
            const accts = (cpty.accounts || []).map(a => ({ ...a }));
            setAccounts(accts);
            setOriginalAccounts(JSON.parse(JSON.stringify(accts)));
        }
    }, [cpty, formFromCpty, isNew]);

    // Dirty check
    const configDirty = JSON.stringify(form) !== JSON.stringify(originalForm);
    const changedFieldCount = Object.keys(form)
        .filter((k) => String(form[k] ?? "") !== String(originalForm[k] ?? ""))
        .length;
    const relsDirty = addedRels.length > 0 || removedRels.length > 0;
    const partnersDirty = addedPartners.length > 0 || removedPartners.length > 0;
    const secretsDirty = Object.keys(secretChanges).length > 0;
    const accountsDirty = JSON.stringify(accounts) !== JSON.stringify(originalAccounts);
    const apiKeyDirty = genericKeyInput !== "";
    const dirty = configDirty || relsDirty || partnersDirty || secretsDirty || accountsDirty || apiKeyDirty;

    // Validation. Code is editable + required only when onboarding; Name is
    // required in both modes. Both are checked for clashes with existing cpties.
    const existingCodes = useMemo(() => new Set(cpties.map((c) => c.cptyCode)), [cpties]);
    const existingNames = useMemo(
        () => new Set(cpties.map((c) => (c.cptyName || "").trim().toLowerCase())),
        [cpties]
    );
    const trimmedName = (form.cptyName || "").trim();
    const codeError = !isNew
        ? null
        : !codeInput.trim()
        ? "Code is required"
        : existingCodes.has(codeInput)
        ? "Code already exists"
        : codeInput.length > MAX_CPTY_CODE_LENGTH
        ? `Max ${MAX_CPTY_CODE_LENGTH} characters`
        : null;
    const nameError = !trimmedName
        ? "Name is required"
        : isNew && existingNames.has(trimmedName.toLowerCase())
        ? "Name already exists"
        : null;
    // Mail Domains is required to onboard: bootstrap-service needs it on the
    // ONBOARD_CPTY payload, and it drives email-domain validation on sign-up.
    const mailDomainsSet = !!(form.mailDomains && form.mailDomains.trim());
    const mailDomainsError = !mailDomainsSet ? "Mail Domains is required" : null;

    // Mirrors the check admin-service enforces on save, so the operator is told
    // here rather than by a 400 after confirming.
    const userReportRecipientError = useMemo(() => {
        if (!form.userReportEnabled) return null;
        const sender = (emailFrom || "").trim().toLowerCase();
        const seen = new Map();
        const lists = [
            ["To", form.userReportToEmails],
            ["Cc", form.userReportCcEmails],
            ["Bcc", form.userReportBccEmails],
        ];
        for (const [label, value] of lists) {
            for (const raw of (value || "").split(",")) {
                const address = raw.trim().toLowerCase();
                if (!address) continue;
                if (sender && address === sender) {
                    return `${label} contains ${raw.trim()}, the address the report is sent from. `
                        + "A mail whose sender is also a recipient is refused by the mail provider, "
                        + `so the report would never arrive. Remove it from ${label}.`;
                }
                const previous = seen.get(address);
                if (previous) {
                    return `${raw.trim()} is listed in both ${previous} and ${label}. `
                        + "Each address may appear once.";
                }
                seen.set(address, label);
            }
        }
        return null;
    }, [form.userReportEnabled, form.userReportToEmails, form.userReportCcEmails,
        form.userReportBccEmails, emailFrom]);
    const onboardReady = isNew && !codeError && !nameError && mailDomainsSet;

    // Any navigation away while dirty (Back, the nav tabs, browser close)
    // goes through the standard Unsaved Changes dialog.
    useUnsavedChangesGuard(
        dirty,
        buildChangeSummary(originalForm, form, addedRels, removedRels, addedPartners, removedPartners)
    );

    const updateField = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        setActionMsg(null);
    };

    // Toggle one weekday in the user-report schedule (weekly cadence). The form
    // holds the days as a comma-joined string; keep MON..SUN order so the saved
    // list is stable and diff-friendly.
    const toggleReportDay = (day) => {
        const set = new Set((form.userReportDaysOfWeek || "").split(",").filter(Boolean));
        if (set.has(day)) set.delete(day);
        else set.add(day);
        updateField("userReportDaysOfWeek", WEEKDAYS.filter((d) => set.has(d)).join(","));
    };

    // Random per-cpty Generic API key: the cpty code plus 128 random bits,
    // the same shape the e2e/bootstrap paths mint. The operator can overwrite
    // it with an existing customer's key before saving.
    const generateApiKey = () => {
        const bytes = new Uint8Array(16);
        window.crypto.getRandomValues(bytes);
        const hex = Array.from(bytes).map((b) => b.toString(16).padStart(2, "0")).join("");
        return `${code}-${hex}`;
    };

    const copyToClipboard = (text) => {
        if (navigator.clipboard) navigator.clipboard.writeText(text);
    };

    // Open the key field pre-filled with a fresh key, revealed and copyable.
    const startKeyEntry = () => {
        setGenericKeyInput(generateApiKey());
        // Masked by default: never render a key in plaintext until the operator
        // explicitly clicks Show (the value is a fresh candidate key, but it
        // still should not sit on screen in the clear).
        setShowGenericKey(false);
        setEditingGenericKey(true);
        setActionMsg(null);
    };

    // Enabling Generic requires a key to exist (auth-gateway rejects Generic
    // traffic otherwise), so turning it on with no key yet opens key entry.
    const handleToggleGeneric = () => {
        const next = !form.genericEnabled;
        updateField("genericEnabled", next);
        if (next && !form.genericKeyConfigured && !genericKeyInput) {
            startKeyEntry();
        }
    };

    // Subscription helpers
    const selectedSubs = (form.refDataSubscription || "").split(",").filter(Boolean);
    const toggleSubscription = (sub) => {
        const current = new Set(selectedSubs);
        if (current.has(sub)) current.delete(sub);
        else current.add(sub);
        updateField("refDataSubscription", [...current].join(","));
    };

    // File format helpers
    const selectedFormats = (form.fileFormats || "").split(",").map(s => s.trim()).filter(Boolean);
    const toggleFormat = (fmt) => {
        const current = new Set(selectedFormats);
        if (current.has(fmt)) current.delete(fmt);
        else current.add(fmt);
        updateField("fileFormats", [...current].join(","));
    };

    // Confirmation SFTP formats: same checkbox UX, but the API field is a single
    // comma-separated string (not an array), so join back on every toggle. Any
    // value already stored outside FILE_FORMAT_OPTIONS is preserved across toggles.
    const selectedSftpFormats = (form.confirmationSftpFormats || "").split(",").map(s => s.trim()).filter(Boolean);
    const toggleSftpFormat = (fmt) => {
        const current = new Set(selectedSftpFormats);
        if (current.has(fmt)) current.delete(fmt);
        else current.add(fmt);
        updateField("confirmationSftpFormats", [...current].join(","));
    };

    // Effective relationships (original + added - removed)
    // All relationships: original + added, with removed still shown
    const allRels = useMemo(() => {
        const original = (cpty?.relationships || []).map(r => r.cptyCode);
        const result = new Set(original);
        addedRels.forEach(c => result.add(c));
        return [...result];
    }, [cpty, addedRels]);

    // Relationship autocomplete - exclude existing (including removed, since undo is available)
    const filteredCpties = useMemo(() => {
        if (!newRelationship) return [];
        const existing = new Set(allRels);
        existing.add(code); // exclude self
        // Ranked, so what was typed comes first: the exact code, then codes
        // starting with it, then anything else matching. Test counterparties
        // carry a prefix (`5YO6FKC2-CPTY-1`), so a plain substring match buries
        // the real one among them.
        const rank = (c) => {
            const code = c.cptyCode.toUpperCase();
            if (code === newRelationship) return 0;
            if (code.startsWith(newRelationship)) return 1;
            if (code.includes(newRelationship)) return 2;
            return 3;
        };
        // Every match, not the first handful: the dropdown scrolls, and a cap
        // hides the one being looked for whenever it sorts late.
        return cpties
            .filter((c) => !existing.has(c.cptyCode))
            .filter((c) =>
                c.cptyCode.toUpperCase().includes(newRelationship) ||
                (c.cptyName || "").toUpperCase().includes(newRelationship)
            )
            .sort((a, b) => rank(a) - rank(b) || a.cptyCode.localeCompare(b.cptyCode));
    }, [newRelationship, cpties, allRels, code]);

    const getCptyName = (cptyCode) => {
        const rel = (cpty?.relationships || []).find(r => r.cptyCode === cptyCode);
        if (rel) return rel.cptyName;
        const c = cpties.find(c => c.cptyCode === cptyCode);
        return c?.cptyName || "";
    };

    const handleAddRelationship = (targetCode) => {
        const target = targetCode || newRelationship;
        if (!target) return;
        // If it was previously removed, just un-remove it
        if (removedRels.includes(target)) {
            setRemovedRels(prev => prev.filter(c => c !== target));
        } else {
            setAddedRels(prev => [...prev, target]);
        }
        setNewRelationship("");
        setShowDropdown(false);
        setRelationshipError(null);
    };

    const handleRemoveRelationship = (knownCode) => {
        // If it was newly added, just un-add it
        if (addedRels.includes(knownCode)) {
            setAddedRels(prev => prev.filter(c => c !== knownCode));
        } else {
            setRemovedRels(prev => [...prev, knownCode]);
        }
    };

    const accountsError = useMemo(() => {
        for (const acct of accounts) {
            if (!acct.name || !acct.name.trim()) return "Account name is required";
            if (acct.name.length > 50) return "Account name must be 50 characters or fewer";
            if (!acct.type) return "Intermediary type is required for all accounts";
            if (!acct.accountNo) return "Account code is required";
            if (acct.type === "KSD" && (acct.accountNo.length !== 10 || !/^\d{10}$/.test(acct.accountNo))) {
                return `KSD account code must be exactly 10 digits (${acct.name})`;
            }
            if (acct.type === "KSFC" && (acct.accountNo.length !== 12 || !/^\d{12}$/.test(acct.accountNo))) {
                return `KSFC account code must be exactly 12 digits (${acct.name})`;
            }
        }
        const names = accounts.map(a => a.name);
        if (new Set(names).size !== names.length) return "Account names must be unique";
        return null;
    }, [accounts]);

    const handleSave = () => {
        if (nameError) {
            setActionMsg(nameError);
            return;
        }
        if (!form.mailDomains || !form.mailDomains.trim()) {
            setActionMsg("Mail Domains is required");
            return;
        }
        if (accountsError) {
            setActionMsg(accountsError);
            return;
        }
        if (form.genericEnabled && !form.genericKeyConfigured && !genericKeyInput) {
            setActionMsg("Set a Generic API key before enabling the Generic API.");
            return;
        }
        const summary = buildChangeSummary(originalForm, form, addedRels, removedRels, addedPartners, removedPartners);
        const secretSummary = Object.keys(secretChanges).map(k => `${k}: (new value set)`).join("\n");
        const accountSummary = accountsDirty ? `Intermediary accounts: ${accounts.length} account(s)` : "";
        const apiKeySummary = genericKeyInput ? "Generic API key: (new value set)" : "";
        const fullSummary = [summary, secretSummary, accountSummary, apiKeySummary].filter(Boolean).join("\n");
        if (!fullSummary) {
            setActionMsg("No changes to save");
            return;
        }
        dispatch(ConfirmActions.Open({
            title: "Save Changes",
            message: fullSummary,
            getDescription: true,
            isDescriptionRequired: true,
            descriptionPlaceholder: "Reason for changes",
            okTitle: "Save",
            cancelTitle: "Cancel",
            onOk: async (comment) => {
                setSaving(true);
                setActionMsg(null);
                try {
                    // One request with the full desired state; the backend
                    // reconciles (publishes only what differs) and the poll
                    // returns only once every change is confirmed propagated.
                    const settings = { ...form };
                    // API-access flags ride at the top level of the request, and
                    // genericKeyConfigured is a read-only indicator; none belong
                    // on the CptyAdminConfig settings object.
                    delete settings.genericEnabled;
                    delete settings.hfsEnabled;
                    delete settings.uiEnabled;
                    delete settings.genericKeyConfigured;
                    normalizeAlias(settings);
                    // Convert email tag strings to arrays for the backend
                    settings.inventorySftpUploadEmails = (form.inventorySftpUploadEmails || "").split(",").filter(Boolean);
                    settings.clientPoolUploadEmails = (form.clientPoolUploadEmails || "").split(",").filter(Boolean);
                    // User-access report: schedule fields + recipient arrays
                    settings.userReportEnabled = !!form.userReportEnabled;
                    settings.userReportCadence = form.userReportCadence || "monthly";
                    settings.userReportDaysOfWeek = (form.userReportDaysOfWeek || "").split(",").filter(Boolean);
                    settings.userReportNthWeekday = form.userReportNthWeekday ? parseInt(form.userReportNthWeekday, 10) : null;
                    settings.userReportTime = form.userReportTime || null;
                    settings.userReportTimezone = form.userReportTimezone || null;
                    settings.userReportToEmails = (form.userReportToEmails || "").split(",").filter(Boolean);
                    settings.userReportCcEmails = (form.userReportCcEmails || "").split(",").filter(Boolean);
                    settings.userReportBccEmails = (form.userReportBccEmails || "").split(",").filter(Boolean);
                    // Merge secret changes (only fields the user explicitly changed)
                    Object.assign(settings, secretChanges);

                    const removedRelSet = new Set(removedRels);
                    const knownCptys = [...new Set([
                        ...(cpty?.relationships || []).map(r => r.cptyCode),
                        ...addedRels,
                    ])].filter(c => !removedRelSet.has(c));

                    const removedPartnerSet = new Set(removedPartners);
                    const partnerByCode = new Map();
                    (cpty?.partners || []).forEach(p => partnerByCode.set(p.cptyCode, p.cptyName));
                    addedPartners.forEach(p => partnerByCode.set(p.code, p.name));
                    const partnerCptys = [...partnerByCode.entries()]
                        .filter(([pCode]) => !removedPartnerSet.has(pCode))
                        .map(([pCode, pName]) => ({ cptyCode: pCode, cptyName: pName }));

                    // Store the Generic API key first so enabling Generic never
                    // races ahead of the key existing. The key is HMAC-stored
                    // via its dedicated endpoint, not the setup-own-cpty flow.
                    if (genericKeyInput) {
                        await dispatch(AdminActions.SetApiKey(code, genericKeyInput, comment));
                    }
                    await dispatch(AdminActions.EditCpty(code, {
                        settings,
                        knownCptys,
                        partnerCptys,
                        accounts,
                        genericEnabled: form.genericEnabled,
                        hfsEnabled: form.hfsEnabled,
                        uiEnabled: form.uiEnabled,
                    }, comment));
                    setActionMsg("Changes saved");
                    setAddedRels([]);
                    setRemovedRels([]);
                    setAddedPartners([]);
                    setRemovedPartners([]);
                    setSecretChanges({});
                    setGenericKeyInput("");
                    setEditingGenericKey(false);
                    setShowGenericKey(false);
                    dispatch(AdminActions.FetchCptyDetail(code));
                } catch (e) {
                    setActionMsg("Save failed: " + e.message);
                } finally {
                    setSaving(false);
                }
            },
        }));
    };

    // Onboard a brand-new cpty from this window (Code editable + mandatory). The
    // whole config form is sent in one setup-own-cpty create, then we land on the
    // new cpty's page. Same shape as edit's save, minus the isExisting flag.
    const handleOnboard = () => {
        if (codeError) { setActionMsg(codeError); return; }
        if (nameError) { setActionMsg(nameError); return; }
        if (!form.mailDomains || !form.mailDomains.trim()) { setActionMsg("Mail Domains is required"); return; }
        if (accountsError) { setActionMsg(accountsError); return; }
        const summary = `Code: ${codeInput}\nName: ${trimmedName}\nMail Domains: ${form.mailDomains}`;
        dispatch(ConfirmActions.Open({
            title: "Onboard New Counterparty",
            message: summary,
            getDescription: true,
            isDescriptionRequired: true,
            descriptionPlaceholder: "Reason for onboarding this counterparty",
            okTitle: "Onboard",
            cancelTitle: "Cancel",
            onOk: async (comment) => {
                setSaving(true);
                setActionMsg(null);
                try {
                    const settings = { ...form, cptyCode: codeInput };
                    delete settings.genericEnabled;
                    delete settings.hfsEnabled;
                    delete settings.uiEnabled;
                    delete settings.genericKeyConfigured;
                    normalizeAlias(settings);
                    settings.inventorySftpUploadEmails = (form.inventorySftpUploadEmails || "").split(",").filter(Boolean);
                    settings.clientPoolUploadEmails = (form.clientPoolUploadEmails || "").split(",").filter(Boolean);
                    // User-access report: schedule fields + recipient arrays
                    settings.userReportEnabled = !!form.userReportEnabled;
                    settings.userReportCadence = form.userReportCadence || "monthly";
                    settings.userReportDaysOfWeek = (form.userReportDaysOfWeek || "").split(",").filter(Boolean);
                    settings.userReportNthWeekday = form.userReportNthWeekday ? parseInt(form.userReportNthWeekday, 10) : null;
                    settings.userReportTime = form.userReportTime || null;
                    settings.userReportTimezone = form.userReportTimezone || null;
                    settings.userReportToEmails = (form.userReportToEmails || "").split(",").filter(Boolean);
                    settings.userReportCcEmails = (form.userReportCcEmails || "").split(",").filter(Boolean);
                    settings.userReportBccEmails = (form.userReportBccEmails || "").split(",").filter(Boolean);
                    Object.assign(settings, secretChanges);

                    const knownCptys = [...new Set(addedRels)];
                    const partnerCptys = addedPartners.map((p) => ({ cptyCode: p.code, cptyName: p.name }));

                    await dispatch(AdminActions.OnboardCpty({
                        settings,
                        knownCptys,
                        partnerCptys,
                        accounts,
                        genericEnabled: form.genericEnabled,
                        hfsEnabled: form.hfsEnabled,
                        uiEnabled: form.uiEnabled,
                        apiKey: genericKeyInput || ("auto-generated-" + Date.now()),
                    }, comment));
                    dispatch(AdminActions.ClearOnboardStatus());
                    // Clear the dirty state so the unsaved-changes guard does not
                    // block navigating to the freshly created cpty.
                    setOriginalForm({ ...form });
                    setAddedRels([]); setRemovedRels([]);
                    setAddedPartners([]); setRemovedPartners([]);
                    setSecretChanges({}); setGenericKeyInput("");
                    setOriginalAccounts(JSON.parse(JSON.stringify(accounts)));
                    setTimeout(() => history.push(`/cpties/${codeInput}`), 0);
                } catch (e) {
                    setActionMsg("Onboarding failed: " + e.message);
                } finally {
                    setSaving(false);
                }
            },
        }));
    };

    const handleDiscard = () => {
        if (isNew) {
            const f = formFromCpty({});
            setForm(f);
            setOriginalForm(f);
            setCodeInput("");
            setAccounts([]);
            setOriginalAccounts([]);
        } else if (cpty) {
            setForm(formFromCpty(cpty));
            setOriginalForm(formFromCpty(cpty));
        }
        setAddedRels([]);
        setRemovedRels([]);
        setAddedPartners([]);
        setRemovedPartners([]);
        setSecretChanges({});
        setGenericKeyInput("");
        setEditingGenericKey(false);
        setShowGenericKey(false);
        setAccounts(JSON.parse(JSON.stringify(originalAccounts)));
        setActionMsg(null);
    };

    // The unsaved-changes guard intercepts this push while dirty.
    const handleBack = () => history.push("/cpties");

    // Hour validation
    const validateHour = (value) => {
        const num = parseInt(value);
        if (isNaN(num) || num < 0 || num > 23) return false;
        return true;
    };

    if (loading && !isNew) return <div className={styles.cptyManagement}><div className={styles.loading}>Loading...</div></div>;
    if (error && !isNew) return <div className={styles.cptyManagement}><div className={styles.error}>{error}</div></div>;
    if (!cpty && !isNew) return null;

    return (
        <div className={`${styles.cptyManagement} ${(dirty || isNew) ? styles.hasStatusBar : ""}`}>
            <div className={styles.header}>
                <div className={styles.title}>
                    <button className={styles.detailBtn} onClick={handleBack}>Back</button>
                    {" "}{isNew ? "Onboard New Counterparty" : cpty.cptyCode}
                </div>
            </div>

            {actionMsg && <div className={styles.actionMsg}>{actionMsg}</div>}

            {/* Configuration */}
            <div className={styles.section}>
                <div className={styles.sectionTitle}>Configuration</div>
                <div className={styles.configForm}>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>Code</span>
                        <input
                            type="text"
                            value={isNew ? codeInput : code}
                            disabled={!isNew}
                            maxLength={MAX_CPTY_CODE_LENGTH}
                            placeholder={isNew ? "e.g. CPTY-1" : undefined}
                            onChange={isNew ? (e) => { setCodeInput(e.target.value.toUpperCase()); setActionMsg(null); } : undefined}
                            className={styles.configInput}
                            data-testid="onboard-code-input"
                        />
                        {codeError && <span className={styles.inlineError}>{codeError}</span>}
                    </div>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>Name</span>
                        <input type="text" value={form.cptyName || ""} onChange={(e) => updateField("cptyName", e.target.value)} className={styles.configInput} data-testid="onboard-name-input" />
                        {nameError && <span className={styles.inlineError}>{nameError}</span>}
                    </div>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>Mail Domains</span>
                        <TagInput value={form.mailDomains} originalValue={originalForm.mailDomains} onChange={(v) => updateField("mailDomains", v)} placeholder="Type domain and press Enter" />
                        {mailDomainsError && <span className={styles.inlineError}>{mailDomainsError}</span>}
                    </div>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>Settlement Auto-Roll Time</span>
                        <select value={form.autoRollUtcHour || 0} onChange={(e) => updateField("autoRollUtcHour", parseInt(e.target.value))} className={styles.configSelect}>
                            {HOURS_BY_KST.map(utc => (
                                <option key={utc} value={utc}>{kstLabel(utc)}</option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>Inventory Auto-Roll Time</span>
                        <select value={form.autoRollInventoryUTCHour || ""} onChange={(e) => updateField("autoRollInventoryUTCHour", e.target.value)} className={styles.configSelect}>
                            <option value="">Not set</option>
                            {HOURS_BY_KST.map(utc => (
                                <option key={utc} value={String(utc)}>{kstLabel(utc)}</option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>Hold Expiry Time</span>
                        <select value={form.eaExpiryUtcHour || ""} onChange={(e) => updateField("eaExpiryUtcHour", e.target.value)} className={styles.configSelect}>
                            <option value="">Not set</option>
                            {HOURS_BY_KST.map(utc => (
                                <option key={utc} value={String(utc)}>{kstLabel(utc)}</option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>Use Client Pool</span>
                        <CheckBox name="useClientPool" selected={form.useClientPool} onChange={() => updateField("useClientPool", !form.useClientPool)} small styleName="black" />
                    </div>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>Use Confirmable Inventory</span>
                        <CheckBox name="useConfirmableInventory" selected={form.useConfirmableInventory} onChange={() => updateField("useConfirmableInventory", !form.useConfirmableInventory)} small styleName="black" />
                    </div>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>Expire Inventory</span>
                        <CheckBox name="expireInventory" selected={form.expireInventory} onChange={() => updateField("expireInventory", !form.expireInventory)} small styleName="black" />
                    </div>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>Holds Expire Same Day</span>
                        <CheckBox name="eaExpireTodayOrTomorrow" selected={form.eaExpireTodayOrTomorrow} onChange={() => updateField("eaExpireTodayOrTomorrow", !form.eaExpireTodayOrTomorrow)} small styleName="black">
                            <span className={styles.configHint}>When enabled, holds expire at the Hold Expiry Time on the same day they are created. When disabled, holds expire the next business day.</span>
                        </CheckBox>
                    </div>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>Post-Trade Processing</span>
                        <CheckBox name="usePostTrade" selected={form.usePostTrade} onChange={() => updateField("usePostTrade", !form.usePostTrade)} small styleName="black" />
                    </div>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>Require MFA</span>
                        <CheckBox name="mfaRequired" selected={form.mfaRequired} onChange={() => updateField("mfaRequired", !form.mfaRequired)} small styleName="black" dataTestId="mfa-required-toggle">
                            <span className={styles.configHint}>When enabled, users must set up MFA and cannot skip the Security Notice. Turning it off deletes enrolled TOTP for this cpty's users (they re-enrol if it is turned back on).</span>
                        </CheckBox>
                    </div>
                </div>
            </div>

            {/* API Access */}
            <div className={styles.section}>
                <div className={styles.sectionTitle}>API Access</div>
                <div className={styles.configForm}>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>Generic API</span>
                        <CheckBox name="genericEnabled" selected={form.genericEnabled} onChange={handleToggleGeneric} small styleName="black" dataTestId="generic-enabled-toggle" />
                    </div>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>Generic API Key</span>
                        <div className={styles.apiKeyControl}>
                            {editingGenericKey ? (
                                <>
                                    <input
                                        type={showGenericKey ? "text" : "password"}
                                        value={genericKeyInput}
                                        onChange={(e) => setGenericKeyInput(e.target.value)}
                                        className={styles.configInput}
                                        placeholder="Enter or generate a key"
                                        data-testid="generic-api-key-input"
                                    />
                                    <Button value={showGenericKey ? "Hide" : "Show"} onClick={() => setShowGenericKey((v) => !v)} classes={[styles.detailBtn]} />
                                    <Button value="Copy" onClick={() => copyToClipboard(genericKeyInput)} classes={[styles.detailBtn]} />
                                    <Button value="Regenerate" onClick={() => { setGenericKeyInput(generateApiKey()); setShowGenericKey(false); }} classes={[styles.detailBtn]} />
                                </>
                            ) : (
                                <>
                                    <span className={styles.configValue}>{form.genericKeyConfigured ? "Key configured" : "No key set"}</span>
                                    <Button value={form.genericKeyConfigured ? "Replace key" : "Generate key"} onClick={startKeyEntry} classes={[styles.detailBtn]} dataTestId="replace-generic-api-key" />
                                </>
                            )}
                        </div>
                    </div>
                    {editingGenericKey && (
                        <div className={styles.configRow}>
                            <span className={styles.configLabel}></span>
                            <span className={styles.configHint}>Copy this key now and share it with the counterparty. It is stored as a one-way hash and cannot be shown again after saving. Replacing it takes effect within a few minutes.</span>
                        </div>
                    )}
                    {form.genericEnabled && (
                        <>
                            <div className={styles.configRow}>
                                <span className={styles.configLabel}>Generic API Alias Host</span>
                                <input
                                    type="text"
                                    value={form.genericApiAliasHost || ""}
                                    onChange={(e) => updateField("genericApiAliasHost", e.target.value.toLowerCase())}
                                    className={styles.configInput}
                                    placeholder="e.g. acme.api.ap-northeast-2.prod.truweb.io"
                                    data-testid="generic-api-alias-host-input"
                                />
                            </div>
                            <div className={styles.configRow}>
                                <span className={styles.configLabel}>Generic API Alias Path</span>
                                <input
                                    type="text"
                                    value={form.genericApiAliasPrefix || ""}
                                    onChange={(e) => updateField("genericApiAliasPrefix", e.target.value.toLowerCase())}
                                    className={styles.configInput}
                                    placeholder="e.g. /acme-1"
                                    data-testid="generic-api-alias-prefix-input"
                                />
                                <span className={styles.configHint}>Optional. Requests to https://&lt;host&gt;&lt;path&gt;/generic/... are served as this counterparty's Generic API. Set both fields, or leave both empty. The path must be unique per host.</span>
                            </div>
                        </>
                    )}
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>HFS API</span>
                        <CheckBox name="hfsEnabled" selected={form.hfsEnabled} onChange={() => updateField("hfsEnabled", !form.hfsEnabled)} small styleName="black">
                            <span className={styles.configHint}>The HFS API key is shared across all counterparties and is managed on the Platform tab.</span>
                        </CheckBox>
                    </div>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>HFS ID</span>
                        <input type="text" value={form.hfsId || ""} onChange={(e) => updateField("hfsId", e.target.value)} className={styles.configInput} data-testid="hfs-id-input" />
                    </div>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>UI API</span>
                        <CheckBox name="uiEnabled" selected={form.uiEnabled} onChange={() => updateField("uiEnabled", !form.uiEnabled)} small styleName="black" />
                    </div>
                </div>
            </div>

            {/* Notification Emails */}
            <div className={styles.section}>
                <div className={styles.sectionTitle}>Notification Emails</div>
                <div className={styles.configForm}>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>Inventory Upload Emails</span>
                        <TagInput value={form.inventorySftpUploadEmails} originalValue={originalForm.inventorySftpUploadEmails} onChange={(v) => updateField("inventorySftpUploadEmails", v)} placeholder="Type email and press Enter" />
                    </div>
                </div>
            </div>

            {/* User-access report */}
            <div className={styles.section}>
                <div className={styles.sectionTitle}>User-access report</div>
                <div className={styles.configForm}>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>Enabled</span>
                        <CheckBox name="userReportEnabled" selected={form.userReportEnabled} onChange={() => updateField("userReportEnabled", !form.userReportEnabled)} small styleName="black" dataTestId="userReport-enabled" />
                    </div>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>Cadence</span>
                        <select value={form.userReportCadence || "monthly"} onChange={(e) => {
                            const cadence = e.target.value;
                            setForm((prev) => {
                                const next = { ...prev, userReportCadence: cadence };
                                // Seed the weekday when switching to monthly: the <select>
                                // only shows MON by default, and re-picking the shown option
                                // fires no onChange, so the stored value must be set here.
                                if (cadence === "monthly" && !(prev.userReportDaysOfWeek || "").split(",").filter(Boolean).length) {
                                    next.userReportDaysOfWeek = "MON";
                                }
                                return next;
                            });
                            setActionMsg(null);
                        }} className={styles.configSelect}>
                            <option value="daily">Daily</option>
                            <option value="weekdays">Weekdays (Mon–Fri)</option>
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                        </select>
                    </div>
                    {form.userReportCadence === "weekly" && (
                        <div className={styles.configRow}>
                            <span className={styles.configLabel}>Days of week</span>
                            {WEEKDAYS.map((day) => (
                                <CheckBox
                                    key={day}
                                    name={"userReportDay-" + day}
                                    selected={(form.userReportDaysOfWeek || "").split(",").includes(day)}
                                    onChange={() => toggleReportDay(day)}
                                    small
                                    styleName="black"
                                >
                                    <span className={styles.configHint}>{day}</span>
                                </CheckBox>
                            ))}
                        </div>
                    )}
                    {form.userReportCadence === "monthly" && (
                        <div className={styles.configRow}>
                            <span className={styles.configLabel}>Weekday</span>
                            <select
                                value={(form.userReportDaysOfWeek || "").split(",").filter(Boolean)[0] || "MON"}
                                onChange={(e) => updateField("userReportDaysOfWeek", e.target.value)}
                                className={styles.configSelect}
                            >
                                {WEEKDAYS.map((day) => (
                                    <option key={day} value={day}>{day}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    {form.userReportCadence === "monthly" && (
                        <div className={styles.configRow}>
                            <span className={styles.configLabel}>Which occurrence</span>
                            <select value={form.userReportNthWeekday || ""} onChange={(e) => updateField("userReportNthWeekday", e.target.value)} className={styles.configSelect}>
                                <option value="">Every</option>
                                <option value="1">1st</option>
                                <option value="2">2nd</option>
                                <option value="3">3rd</option>
                                <option value="4">4th</option>
                                <option value="5">5th</option>
                            </select>
                            <span className={styles.configHint}>e.g. 1st + MON = first Monday of the month</span>
                        </div>
                    )}
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>Time</span>
                        <input type="time" value={form.userReportTime || ""} onChange={(e) => updateField("userReportTime", e.target.value)} className={styles.timeInput} />
                    </div>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>Timezone</span>
                        <input type="text" value={form.userReportTimezone || ""} onChange={(e) => updateField("userReportTimezone", e.target.value)} className={styles.configInput} placeholder="Asia/Seoul" />
                    </div>
                    <div className={styles.configRow} data-testid="userReport-to">
                        <span className={styles.configLabel}>To</span>
                        <TagInput value={form.userReportToEmails} originalValue={originalForm.userReportToEmails} onChange={(v) => updateField("userReportToEmails", v)} placeholder="Type email and press Enter" />
                    </div>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>Cc</span>
                        <TagInput value={form.userReportCcEmails} originalValue={originalForm.userReportCcEmails} onChange={(v) => updateField("userReportCcEmails", v)} placeholder="Type email and press Enter" />
                    </div>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>Bcc</span>
                        <TagInput value={form.userReportBccEmails} originalValue={originalForm.userReportBccEmails} onChange={(v) => updateField("userReportBccEmails", v)} placeholder="Type email and press Enter" />
                    </div>
                    {userReportRecipientError && (
                        <div className={styles.configRow}>
                            <span className={styles.inlineError} data-testid="userReport-recipient-error">
                                {userReportRecipientError}
                            </span>
                        </div>
                    )}
                    {/* Sending and its history act on what is SAVED, so both are
                        inert until the report is enabled on the stored counterparty.
                        The row is still shown, because a control that is absent
                        whenever it is unusable is a control nobody finds. */}
                    {!isNew && cpty && (
                        <UserReportHistory
                            cptyCode={cpty.cptyCode}
                            enabled={!!cpty.userReportEnabled}
                        />
                    )}
                </div>
            </div>

            {/* Confirmation SFTP */}
            <div className={styles.section}>
                <div className={styles.sectionTitle}>Confirmation SFTP</div>
                <div className={styles.configForm}>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>Host</span>
                        <input type="text" value={form.confirmationSftpHost || ""} onChange={(e) => updateField("confirmationSftpHost", e.target.value)} className={styles.configInput} placeholder="sftp.client.com" />
                    </div>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>Port</span>
                        <input type="text" value={form.confirmationSftpPort || ""} onChange={(e) => updateField("confirmationSftpPort", e.target.value)} className={styles.configInput} placeholder="22" />
                    </div>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>Path</span>
                        <input type="text" value={form.confirmationSftpPath || ""} onChange={(e) => updateField("confirmationSftpPath", e.target.value)} className={styles.configInput} placeholder="/uploads/confirmations" />
                    </div>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>Username</span>
                        <input type="text" value={form.confirmationSftpUserName || ""} onChange={(e) => updateField("confirmationSftpUserName", e.target.value)} className={styles.configInput} />
                    </div>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>Password</span>
                        <SecretField configured={!!cpty?.confirmationSftpPasswordConfigured} fieldName="confirmationSftpPassword" onChange={handleSecretChange} />
                    </div>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>Private Key</span>
                        <SecretField configured={!!cpty?.confirmationSftpPrivateKeyConfigured} fieldName="confirmationSftpPrivateKey" onChange={handleSecretChange} multiline />
                    </div>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>File Formats</span>
                        {FILE_FORMAT_OPTIONS.map((fmt) => (
                            <CheckBox key={fmt} name={"sftpFmt-" + fmt} selected={selectedSftpFormats.includes(fmt)} onChange={() => toggleSftpFormat(fmt)} small styleName="black">
                                <span className={styles.configHint}>{fmt}</span>
                            </CheckBox>
                        ))}
                    </div>
                </div>
            </div>

            {/* Recalls SFTP */}
            <div className={styles.section}>
                <div className={styles.sectionTitle}>Recalls SFTP</div>
                <div className={styles.configForm}>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>Host</span>
                        <input type="text" value={form.sftpRecallsHost || ""} onChange={(e) => updateField("sftpRecallsHost", e.target.value)} className={styles.configInput} />
                    </div>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>User</span>
                        <input type="text" value={form.sftpRecallsUser || ""} onChange={(e) => updateField("sftpRecallsUser", e.target.value)} className={styles.configInput} />
                    </div>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>Secrets Path</span>
                        <input type="text" value={form.sftpRecallsSecretsPath || ""} onChange={(e) => updateField("sftpRecallsSecretsPath", e.target.value)} className={styles.configInput} />
                    </div>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>Secrets Key</span>
                        <SecretField configured={!!cpty?.sftpRecallsSecretsKeyConfigured} fieldName="sftpRecallsSecretsKey" onChange={handleSecretChange} />
                    </div>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>Upload Path</span>
                        <input type="text" value={form.sftpRecallsUploadPath || ""} onChange={(e) => updateField("sftpRecallsUploadPath", e.target.value)} className={styles.configInput} />
                    </div>
                </div>
            </div>

            {/* Confirmation Basket Formats */}
            <div className={styles.section}>
                <div className={styles.sectionTitle}>Confirmation Basket Formats</div>
                <div className={styles.checkboxList}>
                    {FILE_FORMAT_OPTIONS.map((fmt) => (
                        <CheckBox key={fmt} name={fmt} selected={selectedFormats.includes(fmt)} onChange={() => toggleFormat(fmt)} small styleName="black">
                            <span className={styles.checkboxLabel}>{fmt}</span>
                        </CheckBox>
                    ))}
                </div>
            </div>

            {/* Market Data Subscriptions */}
            <div className={styles.section}>
                <div className={styles.sectionTitle}>Market Data Subscriptions</div>
                <div className={styles.checkboxList}>
                    {SUBSCRIPTION_OPTIONS.map((sub, i) => (
                        <CheckBox key={sub} name={sub} selected={selectedSubs.includes(sub)} onChange={() => toggleSubscription(sub)} small styleName="black">
                            <span className={styles.checkboxLabel}>{SUBSCRIPTION_LABELS[i]}</span>
                        </CheckBox>
                    ))}
                </div>
            </div>

            {/* Known Counterparties */}
            <div className={styles.section}>
                <div className={styles.sectionTitle}>Known Counterparties</div>
                <div className={styles.addRelationship}>
                    <div className={styles.autocompleteWrapper}>
                        <input
                            type="text"
                            placeholder="Search counterparty..."
                            value={newRelationship}
                            onChange={(e) => { setNewRelationship(e.target.value.toUpperCase()); setShowDropdown(true); }}
                            onFocus={() => setShowDropdown(true)}
                            onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                        />
                        {showDropdown && filteredCpties.length > 0 && (
                            <div className={styles.dropdown}>
                                {filteredCpties.map((c) => (
                                    <div
                                        key={c.cptyCode}
                                        className={styles.dropdownItem}
                                        onMouseDown={() => handleAddRelationship(c.cptyCode)}
                                    >
                                        {c.cptyCode} ({c.cptyName || "-"})
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <button className={styles.primaryBtn} onClick={() => handleAddRelationship()}>
                        Add
                    </button>
                </div>
                {relationshipError && <div className={styles.error}>{relationshipError}</div>}

                {allRels.length > 0 && (
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Code</th>
                                <th>Name</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {allRels.map((relCode) => {
                                const isAdded = addedRels.includes(relCode);
                                const isRemoved = removedRels.includes(relCode);
                                const name = getCptyName(relCode);
                                return (
                                    <tr key={relCode} className={isAdded ? styles.addedRow : isRemoved ? styles.pendingRemoval : undefined}>
                                        <td>{relCode}</td>
                                        <td>{name || "-"}</td>
                                        <td>
                                            {isAdded && <span className={styles.badge}>New</span>}
                                            {isRemoved && <span className={styles.removedBadge}>Removing</span>}
                                        </td>
                                        <td>
                                            {isRemoved ? (
                                                <button
                                                    className={styles.detailBtn}
                                                    onClick={() => setRemovedRels(prev => prev.filter(c => c !== relCode))}
                                                >
                                                    Undo
                                                </button>
                                            ) : (
                                                <button
                                                    className={styles.removeBtn}
                                                    onClick={() => handleRemoveRelationship(relCode)}
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
                {allRels.length === 0 && (
                    <div className={styles.empty}>No known counterparties configured</div>
                )}
            </div>

            {/* Partner Counterparties */}
            <div className={styles.section}>
                <div className={styles.sectionTitle}>Partner Counterparties</div>
                <div className={styles.addRelationship}>
                    <input
                        type="text"
                        placeholder="Partner code (e.g. Lidl1)"
                        value={newPartnerCode}
                        onChange={(e) => setNewPartnerCode(e.target.value)}
                        className={styles.configInput}
                        style={{ maxWidth: 200 }}
                    />
                    <input
                        type="text"
                        placeholder="Partner name"
                        value={newPartnerName}
                        onChange={(e) => setNewPartnerName(e.target.value)}
                        className={styles.configInput}
                        style={{ maxWidth: 300 }}
                    />
                    <button className={styles.primaryBtn} onClick={() => {
                        if (!newPartnerCode.trim()) return;
                        setAddedPartners(prev => [...prev, { code: newPartnerCode.trim(), name: newPartnerName.trim() || newPartnerCode.trim() }]);
                        setNewPartnerCode("");
                        setNewPartnerName("");
                    }}>
                        Add
                    </button>
                </div>

                {(() => {
                    const allPartners = [
                        ...(cpty?.partners || []).map(p => ({ code: p.cptyCode, name: p.cptyName, isNew: false })),
                        ...addedPartners.map(p => ({ code: p.code, name: p.name, isNew: true })),
                    ];
                    return allPartners.length > 0 ? (
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Code</th>
                                    <th>Name</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {allPartners.map(p => {
                                    const isRemoved = removedPartners.includes(p.code);
                                    return (
                                        <tr key={p.code} className={p.isNew ? styles.addedRow : isRemoved ? styles.pendingRemoval : undefined}>
                                            <td>{p.code}</td>
                                            <td>{p.name || "-"}</td>
                                            <td>
                                                {p.isNew && <span className={styles.badge}>New</span>}
                                                {isRemoved && <span className={styles.removedBadge}>Removing</span>}
                                            </td>
                                            <td>
                                                {isRemoved ? (
                                                    <button className={styles.detailBtn} onClick={() => setRemovedPartners(prev => prev.filter(c => c !== p.code))}>
                                                        Undo
                                                    </button>
                                                ) : p.isNew ? (
                                                    <button className={styles.removeBtn} onClick={() => setAddedPartners(prev => prev.filter(x => x.code !== p.code))}>
                                                        Remove
                                                    </button>
                                                ) : (
                                                    <button className={styles.removeBtn} onClick={() => setRemovedPartners(prev => [...prev, p.code])}>
                                                        Remove
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    ) : (
                        <div className={styles.empty}>No partner counterparties configured</div>
                    );
                })()}
            </div>

            {/* Intermediary Accounts */}
            <div className={styles.section}>
                <div className={styles.sectionTitle}>Intermediary Accounts</div>
                {accounts.length > 0 && (
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Intermediary</th>
                                <th>Account Code</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {accounts.map((acct, idx) => (
                                <tr key={idx}>
                                    <td>
                                        <input
                                            type="text"
                                            value={acct.name || ""}
                                            placeholder="Account name"
                                            className={styles.configInput}
                                            style={{ maxWidth: 200 }}
                                            onChange={(e) => {
                                                const next = [...accounts];
                                                next[idx] = { ...next[idx], name: e.target.value };
                                                setAccounts(next);
                                            }}
                                        />
                                    </td>
                                    <td>
                                        <select
                                            value={acct.type || ""}
                                            className={styles.configSelect}
                                            onChange={(e) => {
                                                const next = [...accounts];
                                                next[idx] = { ...next[idx], type: e.target.value };
                                                setAccounts(next);
                                            }}
                                        >
                                            <option value="">Select...</option>
                                            <option value="KSD">KSD</option>
                                            <option value="KSFC">KSFC</option>
                                        </select>
                                    </td>
                                    <td>
                                        <input
                                            type="text"
                                            value={acct.accountNo || ""}
                                            placeholder={acct.type === "KSFC" ? "12 digits" : "10 digits"}
                                            className={styles.configInput}
                                            style={{ maxWidth: 160 }}
                                            onChange={(e) => {
                                                const next = [...accounts];
                                                next[idx] = { ...next[idx], accountNo: e.target.value };
                                                setAccounts(next);
                                            }}
                                        />
                                    </td>
                                    <td>
                                        <button className={styles.removeBtn} onClick={() => {
                                            setAccounts(accounts.filter((_, i) => i !== idx));
                                        }}>
                                            Remove
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
                {accounts.length === 0 && (
                    <div className={styles.empty}>No intermediary accounts configured</div>
                )}
                <div style={{ marginTop: 8 }}>
                    <button className={styles.detailBtn} onClick={() => {
                        setAccounts([...accounts, { name: "", type: "", accountNo: "" }]);
                    }}>
                        Add Account
                    </button>
                </div>
            </div>

            {/* Status bar for unsaved changes - same fixed bottom bar, text
                shape and button styling as the counterparty list's removal bar */}
            {isNew ? (
                <div className={styles.statusBar}>
                    <span>{onboardReady ? "Counterparty ready to onboard" : "Please fill required fields"}</span>
                    <Button
                        value="Discard Changes"
                        onClick={handleDiscard}
                        disabled={saving || (!dirty && !codeInput.trim())}
                        classes={[styles.detailBtn]}
                    />
                    <Button
                        value={saving ? "Onboarding..." : "Confirm Changes"}
                        onClick={handleOnboard}
                        disabled={saving || !onboardReady || !!userReportRecipientError}
                        classes={[styles.removeBtn]}
                        dataTestId="confirm-onboard"
                    />
                </div>
            ) : dirty && (() => {
                const changeCount = changedFieldCount
                    + addedRels.length + removedRels.length
                    + addedPartners.length + removedPartners.length
                    + Object.keys(secretChanges).length
                    + (accountsDirty ? 1 : 0);
                return (
                    <div className={styles.statusBar}>
                        <span>{changeCount} change{changeCount !== 1 ? "s" : ""} ready to apply</span>
                        <Button
                            value="Discard Changes"
                            onClick={handleDiscard}
                            disabled={saving}
                            classes={[styles.detailBtn]}
                        />
                        <Button
                            value={saving ? "Saving..." : "Confirm Changes"}
                            onClick={handleSave}
                            disabled={saving || !!nameError || !!userReportRecipientError}
                            classes={[styles.removeBtn]}
                        />
                    </div>
                );
            })()}
        </div>
    );
};

export default CptyDetail;
